import React from 'react';
// Import Headless UI components for accessible modal functionality
import {
    Dialog,        // Main modal component
    Transition,    // Handles enter/exit animations
    TransitionChild, // Individual animated elements
    DialogPanel,   // Content container
    DialogTitle,   // Accessible title component
    DialogBackdrop // Modal backdrop/overlay
} from '@headlessui/react';
import {X} from "lucide-react";

// TypeScript interface defining the props for our Modal component
interface ModalProps {
    isOpen: boolean;              // Controls modal visibility
    onClose: () => void;         // Function to call when modal should close
    title: string;               // Modal title text
    children: React.ReactNode;    // Modal content
    maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';  // Optional width configuration
}

const Modal = ({
                   isOpen,
                   onClose,
                   title,
                   children,
                   maxWidth = 'md'  // Default to medium width if not specified
               }: ModalProps) => {
    // Map of Tailwind CSS classes for different modal widths
    const maxWidthClasses = {
        sm: 'max-w-sm',    // Small: 24rem (384px)
        md: 'max-w-md',    // Medium: 28rem (448px)
        lg: 'max-w-lg',    // Large: 32rem (512px)
        xl: 'max-w-xl',    // Extra Large: 36rem (576px)
        '2xl': 'max-w-2xl', // 2X Large: 42rem (672px)
        full: 'max-w-full'
    };

    return (
        // Transition component handles the modal's appearance animation
        // appear: Animate on first render
        // show: Controls visibility
        // as={React.Fragment}: Doesn't add extra DOM nodes
        <Transition appear show={isOpen} as={React.Fragment}>
            {/* Dialog is the main modal container */}
            {/* relative z-50: Ensures modal appears above other content */}
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                {/* First TransitionChild handles the backdrop animation */}
                <TransitionChild
                    as={React.Fragment}
                    enter="ease-out duration-300"    // Smooth entry animation
                    enterFrom="opacity-0"            // Start fully transparent
                    enterTo="opacity-100"            // End fully opaque
                    leave="ease-in duration-200"     // Slightly faster exit animation
                    leaveFrom="opacity-100"          // Start fully opaque
                    leaveTo="opacity-0"              // End fully transparent
                >
                    {/* DialogBackdrop: Semi-transparent black overlay */}
                    {/* fixed inset-0: Covers entire screen */}
                    {/* bg-black/30: 30% opaque black background */}
                    <DialogBackdrop className="fixed inset-0 bg-black/30" />
                </TransitionChild>

                {/* Container for vertical centering */}
                {/* fixed inset-0: Covers entire screen */}
                {/* overflow-y-auto: Enables scrolling if modal is too tall */}
                <div className="fixed inset-0 overflow-y-auto">
                    {/* Flex container for horizontal centering */}
                    {/* min-h-full: Ensures minimum full viewport height */}
                    {/* p-4: Padding around modal */}
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        {/* Second TransitionChild handles the modal content animation */}
                        <TransitionChild
                            as={React.Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"  // Start smaller and transparent
                            enterTo="opacity-100 scale-100" // End full size and opaque
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            {/* DialogPanel: The actual modal box */}
                            <DialogPanel className={`w-full ${maxWidthClasses[maxWidth]} ${maxWidth == "full" ? " h-[calc(100vh-2rem)]" : ""} flex flex-col transform rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all z-50`}>
                                <div className="flex items-center justify-between">
                                    <DialogTitle as="h3" className="text-lg font-medium leading-6 text-slate-900">
                                        {title}
                                    </DialogTitle>

                                    <button
                                        type="button"
                                        onClick={onClose}
                                        className="rounded-lg p-1 text-slate-400 hover:text-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        aria-label="Close modal"
                                    >
                                        <X className="h-5 w-5" />
                                    </button>
                                </div>

                                <div className="mt-4 flex-1 overflow-hidden">
                                    {children}
                                </div>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};

export default Modal;